import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtINR, fmtDateTime } from "@/lib/format";
import { FileText, Clock, Truck, CheckCircle2, AlertTriangle, Bell, Factory, Package, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSupplier } from "@/context/SupplierContext";
import RequestConnection from "@/components/RequestConnection";
import { Building2 } from "lucide-react";

export default function SupplierDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selected, isAll, activeBuyer, company, buyers, loaded } = useSupplier();

  const load = () => api.get(`/supplier/dashboard?buyer_org=${selected}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { if (loaded) load(); }, [selected, loaded]);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading…</div>;
  const { counts, updates_required, recent_pos, recent_reminders } = data;
  const firstName = (user?.supplier?.contact_person || user?.name || "").split(" ")[0];

  if (loaded && buyers.length === 0) {
    return (
      <div data-testid="seller-empty-state" className="mx-auto max-w-lg py-16 text-center">
        <div className="rounded-xl border bg-card p-10">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-4 font-heading text-xl font-semibold">Welcome, {firstName}</h1>
          <p className="mt-1 text-sm font-medium text-slate-700">{company || "Your company"}</p>
          <p className="mt-3 text-sm text-muted-foreground">No buyer organizations are connected to your account yet. Once a buyer approves your connection, their purchase orders will appear here.</p>
          <RequestConnection onDone={load} />
        </div>
      </div>
    );
  }

  const acknowledge = async (poId) => {
    try {
      await api.post(`/supplier/pos/${poId}/update`, { acknowledge: true });
      toast.success("PO acknowledged — buyer notified");
      load();
    } catch (e) { toast.error("Could not acknowledge"); }
  };

  return (
    <div data-testid="supplier-dashboard" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="supplier-greeting">Welcome, {firstName}</h1>
        <p className="text-sm text-muted-foreground">
          Seller: <span className="font-medium text-slate-700">{company || user?.supplier?.name}</span>
          {" · "}Viewing: <span className="font-semibold text-slate-900" data-testid="viewing-buyer-label">{isAll ? "All Buyer Organizations" : activeBuyer?.name}</span>
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {updates_required.length > 0
            ? <span data-testid="supplier-attention-summary" className="font-medium text-[#D97706]">{updates_required.length} POs need your action</span>
            : "You're all caught up."}
        </p>
      </div>

      {isAll && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="all-buyers-breakdown">
          {buyers.map((b) => (
            <div key={b.org_id} className="flex items-center gap-3 rounded-lg border bg-card p-3">
              <Building2 className="h-4 w-4 shrink-0 text-slate-500" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{b.name}</p>
                <p className="text-xs text-muted-foreground">{b.active_pos} active POs{b.on_time_pct != null ? ` · ${b.on_time_pct}% on-time` : ""}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total POs" value={counts.total} icon={FileText} testId="sup-kpi-total" />
        <StatCard label="Awaiting Ack" value={counts.pending_ack} icon={Clock} tone="amber" testId="sup-kpi-ack" />
        <StatCard label="Manufacturing" value={counts.manufacturing} icon={Factory} tone="blue" testId="sup-kpi-mfg" />
        <StatCard label="Ready to Dispatch" value={counts.ready_for_dispatch} icon={Package} tone="blue" testId="sup-kpi-ready" />
        <StatCard label="In Transit" value={counts.in_transit} icon={Truck} tone="blue" testId="sup-kpi-transit" />
        <StatCard label="Due Soon" value={counts.due_soon} icon={CalendarClock} tone="amber" testId="sup-kpi-due" />
        <StatCard label="Delayed" value={counts.delayed} icon={AlertTriangle} tone="red" testId="sup-kpi-delayed" />
        <StatCard label="Delivered" value={counts.delivered} icon={CheckCircle2} tone="green" testId="sup-kpi-delivered" />
      </div>

      {updates_required.length > 0 && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FEF3C7]/40">
          <h2 className="border-b border-[#FDE68A] px-5 py-3 font-heading text-base font-semibold text-[#92400E]">Updates Required</h2>
          <div className="divide-y divide-[#FDE68A]/60">
            {updates_required.map((u, i) => (
              <div key={`${u.po_id}-${u.action}`} data-testid={`supplier-update-${i}`} className="flex items-center gap-4 px-5 py-3">
                <span className={`h-2 w-2 rounded-full ${u.severity === "red" ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`} />
                <div className="flex-1">
                  <button data-testid={`supplier-update-po-${i}`} onClick={() => navigate(`/seller/pos/${u.po_id}`)} className="font-mono text-sm font-semibold hover:underline">{u.po_number}</button>
                  <p className="text-xs text-muted-foreground">
                    {isAll && u.buyer_org_name && <span className="mr-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{u.buyer_org_name}</span>}
                    {u.item} · {u.action}
                  </p>
                </div>
                {u.action === "Acknowledge PO" ? (
                  <Button size="sm" data-testid={`supplier-ack-${i}`} onClick={() => acknowledge(u.po_id)} className="bg-slate-900 hover:bg-slate-800">Acknowledge</Button>
                ) : (
                  <Button size="sm" variant="outline" data-testid={`supplier-update-btn-${i}`} onClick={() => navigate(`/seller/pos/${u.po_id}`)}>Update Now</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border bg-card lg:col-span-2">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Recent Purchase Orders</h2>
          <table className="w-full text-sm" data-testid="supplier-recent-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-2.5 font-medium">PO</th>
                <th className="px-5 py-2.5 font-medium">Item</th>
                <th className="px-5 py-2.5 font-medium text-right">Value</th>
                <th className="px-5 py-2.5 font-medium">Expected</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent_pos.map((p) => (
                <tr key={p.id} data-testid={`supplier-po-row-${p.po_number}`} onClick={() => navigate(`/seller/pos/${p.id}`)} className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono font-medium">{p.po_number}</td>
                  <td className="max-w-44 truncate px-5 py-3 text-muted-foreground">{p.item}</td>
                  <td className="px-5 py-3 text-right font-mono">{fmtINR(p.value)}</td>
                  <td className="px-5 py-3">{fmtDate(p.expected_delivery_date)}</td>
                  <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border bg-card">
          <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-base font-semibold"><Bell className="h-4 w-4" /> Buyer Reminders</h2>
          <div className="divide-y">
            {recent_reminders.length === 0 && <p className="p-5 text-sm text-muted-foreground">No reminders received.</p>}
            {recent_reminders.map((r) => (
              <div key={r.id} className="px-5 py-3">
                <p className="text-xs text-muted-foreground"><span className="font-mono">{r.po_number}</span> · {r.channel} · <span className="font-mono">{fmtDateTime(r.created_at)}</span></p>
                <p className="mt-1 line-clamp-2 text-sm">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
